import type { CoreUITheme } from '@scality/core-ui';
import { Card, Checkbox, Stack, Text, Wrap } from '@scality/core-ui';
import { plum, yellow } from '@scality/core-ui/dist/style/theme';
import { Fragment } from 'react';
import styled from 'styled-components';
import { useIsInPanel } from '../../../dualPanel/InPanelContext';
import { parseInstanceMarker } from '../../../portal/instanceMarker';
import type { AIToolComponent } from '../../chat-messages/GuardianMarkdown';
import { InstanceBadge } from '../../InstanceBadge';

const PRIORITY_LEVELS = ['p1', 'p2', 'p3', 'service_request'] as const;
type PriorityLevel = (typeof PRIORITY_LEVELS)[number];
type ThemeStatusColor = keyof Pick<CoreUITheme, 'statusCritical' | 'statusWarning'>;

// Detects a RING product from its label without depending on the exact
// backend/Zendesk string (e.g. "RING (including S3C)") — only used to pick
// which version label to show, not to constrain what a product can be.
const isRingProduct = (product?: string | null): boolean => product?.toLowerCase().includes('ring') ?? false;

export type ZendeskTicketParams = {
  subject: string;
  description: string;
  priority_level?: PriorityLevel;
  ticket_form?: string | null;
  product?: string | null;
  install_base_id?: string | null;
  environment?: string | null;
  version?: string | null;
  customer_critical?: boolean | null;
  business_impact_details?: string | null;
  company_ticket_id?: string | null;
  cc?: string[] | null;
  guardian_filled?: string[];
  zendesk_ticket_id?: string | null;
};

// Priority is shown as a small coloured dot + label (no attention-grabbing badge), per
// design: P1 violet, P2 red, P3 orange, Service Request yellow. core-ui's theme only
// exposes statusCritical/statusWarning (no violet/yellow status tokens), so P1 and
// Service Request use the package's raw brand color constants instead of theme tokens.
const PRIORITY_LEVEL_LABEL: Record<PriorityLevel, string> = {
  p1: 'P1',
  p2: 'P2',
  p3: 'P3',
  service_request: 'Service Request',
};

const PRIORITY_LEVEL_COLOR: Record<PriorityLevel, string | ThemeStatusColor> = {
  p1: plum,
  p2: 'statusCritical',
  p3: 'statusWarning',
  service_request: yellow,
};

const isThemeStatusColor = (color: string | ThemeStatusColor): color is ThemeStatusColor =>
  color === 'statusCritical' || color === 'statusWarning';

const FIELD_PADDING_X = '20px';

/**
 * Inline in the chat the card is a fixed-width aside beside the reply; in the panel it is the
 * whole of the view, so it takes the width the panel gives it (see MainPanelBody's gutters).
 */
const INLINE_WIDTH = '380px';

const Panel = styled(Card)`
  && {
    border: 1px solid ${({ theme }) => theme.border};
    border-radius: 6px;
  }

  /* Align the header's horizontal padding with the field rows below. */
  && .sc-cardheader {
    padding: 14px ${FIELD_PADDING_X};
  }
`;

const FieldRow = styled.div`
  padding: 10px ${FIELD_PADDING_X};
`;

// "Proposed by Guardian" — intentionally subtle and below the design system's
// contrast threshold (deliberate per design); has no core-ui equivalent.
const GuardianBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-size: 0.71rem;
  color: ${({ theme }) => theme.infoPrimary};
  border: 1px solid ${({ theme }) => theme.infoSecondary};
  border-radius: 3px;
  padding: 0 5px;
  line-height: 1.8;
  white-space: nowrap;
`;

const PriorityDot = styled.span<{ $color: string | ThemeStatusColor }>`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  flex-shrink: 0;
  background: ${({ theme, $color }) => (isThemeStatusColor($color) ? theme[$color] : $color)};
`;

const Field: React.FC<{ label: string; guardian?: boolean; children: React.ReactNode }> = ({
  label,
  guardian = false,
  children,
}) => (
  <FieldRow>
    <Wrap alignItems="center" mb="3px">
      <Text variant="Small" color="textSecondary">
        {label}
      </Text>
      {guardian && (
        <GuardianBadge>
          <span aria-hidden="true">✦</span> Proposed by Guardian
        </GuardianBadge>
      )}
    </Wrap>
    {children}
  </FieldRow>
);

/** Renders a multi-paragraph string: blank lines become paragraphs (small gap), single newlines become line breaks. */
const Paragraphs: React.FC<{ text: string }> = ({ text }) => (
  <>
    {text.split('\n\n').map((para, i) => (
      <Text
        // eslint-disable-next-line react/no-array-index-key
        key={i}
        variant="Small"
        color="textPrimary"
        style={{ display: 'block', lineHeight: 1.5, marginTop: i === 0 ? 0 : '0.35em' }}
      >
        {para.split('\n').map((line, j) => (
          <Fragment key={j}>
            {j > 0 && <br />}
            {line}
          </Fragment>
        ))}
      </Text>
    ))}
  </>
);

const PriorityDotForLevel: React.FC<{ level: PriorityLevel }> = ({ level }) => (
  <PriorityDot $color={PRIORITY_LEVEL_COLOR[level] ?? 'statusHealthy'} />
);

// Placeholder for an optional field the user hasn't filled in yet — shown instead of
// hiding the row entirely, so the draft surfaces every field that could still be set.
const EMPTY_PLACEHOLDER = '–';

const OptionalText: React.FC<{ value?: string | null }> = ({ value }) =>
  value != null && value !== '' ? (
    <Text variant="Small" color="textPrimary">
      {value}
    </Text>
  ) : (
    <Text variant="Small" color="textSecondary" style={{ opacity: 0.5 }}>
      {EMPTY_PLACEHOLDER}
    </Text>
  );

const DeploymentValue: React.FC<{ value?: string | null }> = ({ value }) => {
  const instanceId = value != null ? parseInstanceMarker(value) : null;
  return instanceId != null ? <InstanceBadge id={instanceId} /> : <OptionalText value={value} />;
};

export const ZendeskTicket: AIToolComponent<ZendeskTicketParams> = ({
  params: {
    subject,
    description,
    priority_level = 'p3',
    ticket_form,
    product,
    install_base_id,
    environment,
    version,
    customer_critical,
    business_impact_details,
    company_ticket_id,
    cc,
    guardian_filled = [],
    zendesk_ticket_id,
  },
}) => {
  const inPanel = useIsInPanel();
  const isFilled = (field: string) => guardian_filled.includes(field);
  const versionLabel = isRingProduct(product) ? 'RING Version' : 'ARTESCA Version';

  const fullDescription =
    description +
    (customer_critical && business_impact_details ? `\n\nBusiness impact: ${business_impact_details}` : '');

  const isSent = zendesk_ticket_id != null;

  return (
    <Panel width={inPanel ? '100%' : INLINE_WIDTH} headerBackgroundColor="backgroundLevel2">
      <Card.Header>
        <Text variant="Small" color="textPrimary" isEmphazed>
          {isSent ? `Support request #${zendesk_ticket_id}` : 'Support request draft'}
        </Text>
        <Text variant="Smaller" color="textSecondary" style={{ display: 'block', marginTop: 2 }}>
          {isSent ? 'Our support team will follow up with you.' : 'Just say if anything needs adjusting.'}
        </Text>
      </Card.Header>

      <Card.Body>
        <Field label="Subject" guardian={isFilled('subject')}>
          <Text variant="Small" color="textPrimary">
            {subject}
          </Text>
        </Field>

        <Field label="Description" guardian={isFilled('description')}>
          <Paragraphs text={fullDescription} />
          <Text variant="Smaller" color="textSecondary" style={{ display: 'block', marginTop: 6 }}>
            Make it actionable: include the observed behavior, what you expected instead, and the business impact.
          </Text>
        </Field>

        <Field label="Priority" guardian={isFilled('priority_level')}>
          <Stack gap="r8">
            <PriorityDotForLevel level={priority_level} />
            <Text variant="Small" color="textPrimary">
              {PRIORITY_LEVEL_LABEL[priority_level] ?? priority_level}
            </Text>
          </Stack>
        </Field>

        <Field label="Form type" guardian={isFilled('ticket_form')}>
          <OptionalText value={ticket_form} />
        </Field>

        <Field label="Product" guardian={isFilled('product')}>
          <OptionalText value={product} />
        </Field>

        <Field label="Deployment">
          <DeploymentValue value={install_base_id} />
        </Field>

        {/* A ticket is either RING or ARTESCA, never both — only one version field
            applies. Defaults to ARTESCA Version when the product doesn't clearly
            indicate RING (e.g. not set yet). */}
        <Field label={versionLabel} guardian={isFilled('version')}>
          <OptionalText value={version} />
        </Field>

        <Field label="Environment" guardian={isFilled('environment')}>
          <OptionalText value={environment} />
        </Field>

        <Field label="Business Impact" guardian={isFilled('customer_critical')}>
          {customer_critical != null ? (
            <Checkbox
              checked={customer_critical}
              disabled
              label={customer_critical ? 'Impacting our business' : 'Not impacting'}
            />
          ) : (
            <OptionalText value={null} />
          )}
        </Field>

        <Field label="Company Ticket ID" guardian={isFilled('company_ticket_id')}>
          <OptionalText value={company_ticket_id} />
        </Field>

        <Field label="CC" guardian={isFilled('cc')}>
          <OptionalText value={cc != null && cc.length > 0 ? cc.join(', ') : null} />
        </Field>
      </Card.Body>
    </Panel>
  );
};

ZendeskTicket.description = 'Displays a read-only Zendesk ticket draft card for the user to review before sending.';

ZendeskTicket.schema = {
  type: 'object',
  properties: {
    subject: { type: 'string' },
    description: { type: 'string' },
    priority_level: { type: 'string', enum: [...PRIORITY_LEVELS] },
    ticket_form: { type: 'string' },
    product: { type: 'string' },
    install_base_id: { type: 'string' },
    environment: { type: 'string' },
    version: { type: 'string' },
    customer_critical: { type: 'boolean' },
    business_impact_details: { type: 'string' },
    company_ticket_id: { type: 'string' },
    cc: { type: 'array', items: { type: 'string' } },
    guardian_filled: { type: 'array', items: { type: 'string' } },
    pending_review: { type: 'array', items: { type: 'string' } },
    // The backend now always emits a string id. `integer` is still accepted here
    // because this schema also validates blocks written before that change: a
    // conversation still open at deploy time carries an integer id in its earlier
    // messages, and a mismatch renders an error instead of the card.
    zendesk_ticket_id: { type: ['integer', 'string'] },
  },
  required: ['subject', 'description'],
};

ZendeskTicket.displayInPanel = true;
// One "Ticket" tab showing only the latest revision — the ticket is revised in place, not stacked.
ZendeskTicket.panelPlacement = { strategy: 'replace', role: 'tickets', title: 'Ticket' };
